import React, { useState } from 'react';
import { Play, X } from 'lucide-react';
import CTASection from '../components/CTASection';
import { PORTFOLIO, DESIGN_PHOTOGRAPHY } from '../mock';
import useSEO from '../hooks/useSEO';

const Portfolio = () => {
  const [lightbox, setLightbox] = useState(null);
  useSEO({
    title: 'Portfolio, Marketing Agency Liverpool | Yellow',
    description: 'See work from Yellow, a marketing agency Liverpool. Films for global brands plus design and photography that stops the scroll, LJMU, Persil, Cancer Research UK, Land Rover.',
    path: '/portfolio'
  });

  return (
    <>
      <section className="pt-36 pb-14 lg:pt-44">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <p className="section-label mb-6">Portfolio</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02] max-w-5xl">
            Selected work, <span className="text-[#F5CD1B]">the highlights.</span>
          </h1>
          <p className="text-white/70 text-xl leading-relaxed mt-8 max-w-3xl">
            Over 1,000 projects delivered. Below are a few we can’t stop talking about, from brand campaigns to internal comms and product films.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <p className="section-label mb-3">Video Content</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.05]">
              Films that <span className="text-[#F5CD1B]">move people.</span>
            </h2>
          </div>
          <FilteredGrid filter="All" />
        </div>
      </section>

      {/* Design & Photography Mosaic */}
      <section className="py-20 lg:py-24 bg-[#08080a]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <p className="section-label mb-3">Design & Photography</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.05]">
              Images that <span className="text-[#F5CD1B]">stop the scroll.</span>
            </h2>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
            {DESIGN_PHOTOGRAPHY.map((item) => (
              <button
                key={item.id}
                onClick={() => setLightbox(item)}
                className="group relative block w-full overflow-hidden rounded-xl bg-[#111114] border border-white/5 break-inside-avoid hover:border-[#F5CD1B]/30 transition-colors"
              >
                <img src={item.src} alt={`${item.title}, ${item.tag} by Yellow, marketing agency Liverpool`} className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white p-2 hover:text-[#F5CD1B]" onClick={() => setLightbox(null)}><X size={28} /></button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={`${lightbox.title}, ${lightbox.tag} by Yellow, marketing agency Liverpool`} className="w-full h-auto max-h-[85vh] object-contain rounded-xl" />
          </div>
        </div>
      )}

      <CTASection />
    </>
  );
};

const FilteredGrid = ({ filter }) => {
  // Reuse PortfolioGrid but filter mock inline via mock
  const filtered = filter === 'All' ? PORTFOLIO : PORTFOLIO.filter(p => p.category === filter);
  return (
    <div>
      <PortfolioGridInline items={filtered} />
    </div>
  );
};

const PortfolioGridInline = ({ items }) => {
  const [active, setActive] = useState(null);
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <button
            key={p.id}
            onClick={() => setActive(p)}
            className="card-hover group relative overflow-hidden rounded-xl aspect-[4/3] bg-[#111114] border border-white/5 text-left"
          >
            <img src={p.thumb} alt={`${p.title}, video content by Yellow, marketing agency Liverpool`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={(e)=>{e.target.style.display='none'}} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0d] via-transparent to-transparent opacity-70"></div>
            <div className="absolute top-4 left-4">
              <span className="text-[10px] uppercase tracking-widest text-[#F5CD1B] bg-[#0b0b0d]/70 px-2 py-1 rounded-md border border-[#F5CD1B]/20">{p.category}</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-14 h-14 rounded-full bg-[#F5CD1B] flex items-center justify-center">
                <Play size={20} className="text-[#0b0b0d] ml-1" fill="currentColor" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <h4 className="font-display text-xl font-bold text-white">{p.title}</h4>
              <p className="text-white/60 text-xs mt-1">{p.client}</p>
            </div>
          </button>
        ))}
      </div>
      {active && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <button className="absolute top-6 right-6 text-white p-2 hover:text-[#F5CD1B]" onClick={() => setActive(null)}><X size={28} /></button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${active.videoId || 'GPwWTT5rdro'}?autoplay=1&rel=0`} title={active.title} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen />
            </div>
            <div className="mt-4 flex items-start justify-between gap-4 text-white">
              <div>
                <p className="section-label">{active.category}</p>
                <h3 className="font-display text-2xl font-bold mt-1">{active.title}</h3>
                <p className="text-white/60 text-sm mt-1">Client: {active.client}</p>
              </div>
              {active.videoId && (
                <a href={`https://www.youtube.com/watch?v=${active.videoId}`} target="_blank" rel="noopener noreferrer" className="btn-yellow inline-flex items-center gap-2 px-5 py-3 rounded-md uppercase text-xs whitespace-nowrap">
                  Watch on YouTube <Play size={14} fill="currentColor" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Portfolio;
